import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";

const ALL_PERMS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "purchases", label: "Pembelian" },
  { key: "sales", label: "Penjualan" },
  { key: "payments", label: "Bayar Cicilan" },
  { key: "calculator", label: "Kalkulator Cicilan" },
  { key: "overdue", label: "Keterlambatan" },
  { key: "customers", label: "Pelanggan" },
  { key: "cars", label: "Mobil" },
  { key: "catalog", label: "Katalog Mobil" },
  { key: "interest-rates", label: "Bunga Kredit" },
  { key: "late-categories", label: "Kategori Telat" },
  { key: "settings", label: "Pengaturan" },
  { key: "users", label: "Pengguna" },
  { key: "import", label: "Import Excel" },
  { key: "audit", label: "Audit & Backup" },
];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { email: "", name: "", role: "kasir", permissions: [], password: "" };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...empty, ...u, password: "" }); setOpen(true); };

  const togglePerm = (key) => {
    const cur = form.permissions || [];
    setForm({ ...form, permissions: cur.includes(key) ? cur.filter((p) => p !== key) : [...cur, key] });
  };

  const submit = async () => {
    try {
      if (!form.email || !form.name) return toast.error("Email & nama wajib");
      if (!editing && !form.password) return toast.error("Password wajib untuk user baru");
      if (editing) await api.put(`/users/${editing.id}`, form);
      else await api.post("/users", form);
      toast.success("Tersimpan");
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Terhapus"); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    }
  };

  return (
    <div>
      <PageHeader title="Pengguna Aplikasi" subtitle="Kelola akun & hak akses menu per user"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="new-user-btn" className="rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-10">
                <Plus size={14} className="mr-2" /> Tambah User
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-xl max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit" : "Tambah"} User</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label className="text-xs uppercase tracking-wider">Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-none" data-testid="user-name" /></div>
                  <div><Label className="text-xs uppercase tracking-wider">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 rounded-none" data-testid="user-email" /></div>
                  <div><Label className="text-xs uppercase tracking-wider">Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger className="mt-2 rounded-none" data-testid="user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin (semua akses)</SelectItem>
                        <SelectItem value="kasir">Kasir (akses terbatas)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs uppercase tracking-wider">Password {editing && "(kosongkan bila tidak diubah)"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 rounded-none" data-testid="user-password" /></div>
                </div>
                {form.role !== "admin" && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Hak Akses Menu</Label>
                    <div className="mt-3 grid grid-cols-2 gap-2 border border-gray-200 p-3 max-h-64 overflow-y-auto">
                      {ALL_PERMS.map((p) => (
                        <label key={p.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-2 py-1">
                          <input
                            type="checkbox"
                            checked={(form.permissions || []).includes(p.key)}
                            onChange={() => togglePerm(p.key)}
                            data-testid={`perm-${p.key}`}
                          />
                          {p.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <Button onClick={submit} data-testid="user-submit" className="w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11">Simpan</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-4 md:p-6 lg:p-10">
        <div className="border border-gray-200 overflow-x-auto overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                <th className="p-4">Nama</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Akses</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-gray-100">
                  <td className="p-4 font-semibold">{u.name}</td>
                  <td className="p-4 font-mono-num text-xs">{u.email}</td>
                  <td className="p-4"><span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${u.role === "admin" ? "bg-gray-900 text-white" : "border border-gray-900"}`}>{u.role}</span></td>
                  <td className="p-4 text-xs text-gray-600">{u.role === "admin" ? "Semua menu" : `${(u.permissions || []).length} menu`}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => openEdit(u)} className="mr-3 text-gray-500 hover:text-black"><PencilSimple size={16} /></button>
                    <button onClick={() => del(u.id)} className="text-gray-500 hover:text-[#E63946]"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
